import { Link } from "react-router-dom";
import { Footprints, Home, Car, Trees, ScrollText, Star } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSite } from "@/context/SiteContext";

const Item = ({ icon: Icon, title, children, highlight, testid }) => (
  <div className={`p-6 sm:p-8 border border-border rounded-lg ${highlight ? "bg-[hsl(35_70%_94%)] border-[hsl(30_50%_75%)]" : "bg-white"}`} data-testid={testid}>
    <Icon className="w-5 h-5 text-[hsl(var(--sage))]" strokeWidth={1.5} />
    <p className="font-serif text-2xl mt-4">{title}</p>
    <div className="text-sm text-muted-foreground mt-2 leading-relaxed">{children}</div>
  </div>
);

export const GoodToKnow = () => {
  const { site } = useSite();
  const rules = [...(site?.content.house_rules || []), ...(site?.policies.extra_rules || [])];
  return (
    <section className="section-pad bg-[hsl(var(--cream))]" id="good-to-know" data-testid="good-to-know-section">
      <div className="max-w-7xl mx-auto">
        <p className="eyebrow">Before you book</p>
        <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-medium">Good to Know Before You Book</h2>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Item icon={Footprints} title="Stair access required" highlight testid="gtk-stairs">Guests must be able to climb stairs to reach the apartment. The studio is not step-free or wheelchair accessible.</Item>
          <Item icon={Home} title="Private studio setting" testid="gtk-private">The apartment is in a separate building on the same property as the owner's home. You have your own space, and the owner is nearby if you need help.</Item>
          <Item icon={Car} title="Parking" testid="gtk-parking">Free parking is available on the property.</Item>
          <Item icon={Trees} title="Residential neighborhood" testid="gtk-residential">The studio sits in a residential neighborhood, about one to two blocks from the main roads around Calder Avenue and 11th Street.</Item>
          <Item icon={ScrollText} title="House rules" testid="gtk-rules">
            <ul className="space-y-1 list-disc pl-4">{rules.map((r, i) => <li key={i}>{r}</li>)}</ul>
          </Item>
        </div>
      </div>
    </section>
  );
};

export const Reputation = () => {
  const { site } = useSite();
  const c = site?.content;
  if (!c?.show_rating) return null;
  return (
    <section className="px-5 sm:px-8 lg:px-12 py-16" data-testid="reputation-section">
      <div className="max-w-3xl mx-auto text-center">
        <div className="flex justify-center gap-1 text-[hsl(var(--sage))]">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-4 h-4 fill-current" />)}</div>
        <p className="font-serif text-3xl sm:text-4xl mt-4" data-testid="reputation-rating">Rated {c.rating} out of 5 across {c.review_count} reviews on Airbnb</p>
        {c.guest_favorite && <p className="mt-2 text-sm text-muted-foreground">Recognized as an Airbnb Guest Favorite</p>}
        <p className="mt-3 text-xs text-muted-foreground">Rating and reviews originate from the property's Airbnb listing.</p>
      </div>
    </section>
  );
};

export const Faqs = () => {
  const { site } = useSite();
  return (
    <section id="faqs" className="section-pad scroll-mt-16 bg-white border-t border-border" data-testid="faqs-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <p className="eyebrow">FAQs</p>
          <h2 className="mt-4 text-3xl sm:text-4xl font-medium">Questions, answered</h2>
          <Link to="/book" className="inline-block mt-8 bg-primary text-primary-foreground px-6 py-3 rounded-full text-sm hover:bg-[hsl(var(--sage))] transition-colors" data-testid="faq-book-button">Book Your Stay</Link>
        </div>
        <Accordion type="single" collapsible className="lg:col-span-8">
          {site?.content.faqs.map((f, i) => (
            <AccordionItem key={i} value={`f${i}`}>
              <AccordionTrigger className="text-left font-serif text-xl hover:no-underline" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};
